/**
 * IrrigationLiveMap
 *
 * The hero map on the irrigation landing page. Every farm at once, with the
 * question "what is watering right now?" answered by the drawing itself:
 *
 *   - a valve with a drawn area  → that area fills in its zone colour and its
 *     outline animates, captioned with the time left
 *   - a valve without one        → a pulsing dot on its node, so an unmapped
 *     zone is still visible instead of silently missing
 *   - everything idle            → faint outlines, so the layout of the farm
 *     still reads without competing with the live state
 *
 * Read-only. Drawing and editing live on the controller detail page.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Polygon,
  TileLayer,
  Tooltip,
  useMap,
} from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Layers } from 'lucide-react';
import type {
  IrrigationOverviewData,
  OverviewFarm,
  GeoJSONPolygon,
} from '../../services/irrigationOverview';
import { formatRunTime, type ZoneRun } from '../../utils/irrigationRuns';
import { relayDisplayName } from '../../services/espnowIrrigation';
import { TILE_LAYERS, type TileLayerKey } from '../../utils/leafletGeo';
import '../../styles/IrrigationLive.css';
import { formatNum, toNum } from '@/utils/numeric';
import { nodeState, nodeStateLabel } from '../../utils/nodeState';

// GeoJSON stores [lng, lat]; Leaflet wants [lat, lng].
function ringToLatLng(ring: number[][]): [number, number][] {
  return ring.map(([lng, lat]) => [lat, lng]);
}

interface IrrigationLiveMapProps {
  data: IrrigationOverviewData;
  runs: ZoneRun[];
  /** null = every farm. */
  focusFarmId: number | null;
  height?: number;
}

/**
 * Fit to whatever is currently in scope. Re-runs when the farm filter changes
 * but not on every poll, so the map does not yank itself back while someone is
 * panning around looking at a block.
 */
function FitBounds({ points, fitKey }: { points: [number, number][]; fitKey: string }) {
  const map = useMap();
  const lastFit = useRef<string | null>(null);

  useEffect(() => {
    if (points.length === 0 || lastFit.current === fitKey) return;
    // No animation: a zoom still in flight when the page unmounts throws from Leaflet.
    map.fitBounds(L.latLngBounds(points), { padding: [28, 28], maxZoom: 17, animate: false });
    lastFit.current = fitKey;
  }, [map, points, fitKey]);

  return null;
}

export default function IrrigationLiveMap({
  data,
  runs,
  focusFarmId,
  height = 460,
}: IrrigationLiveMapProps) {
  const [layer, setLayer] = useState<TileLayerKey>('satellite');

  const farms: OverviewFarm[] = useMemo(
    () => (focusFarmId == null ? data.farms : data.farms.filter((f) => f.id === focusFarmId)),
    [data.farms, focusFarmId]
  );

  const runByKey = useMemo(() => new Map(runs.map((r) => [r.key, r])), [runs]);

  /** Running valves in scope that have no drawn area — shown as pulsing dots. */
  const undrawnRuns = useMemo(
    () =>
      runs.filter(
        (r) =>
          !r.boundary &&
          r.lat != null &&
          r.lng != null &&
          (focusFarmId == null || r.farmId === focusFarmId)
      ),
    [runs, focusFarmId]
  );

  const points = useMemo(() => {
    const pts: [number, number][] = [];
    const pushRing = (geom: GeoJSONPolygon | null) => {
      for (const [lng, lat] of geom?.coordinates?.[0] ?? []) pts.push([lat, lng]);
    };
    for (const farm of farms) {
      pushRing(farm.boundary_geometry);
      for (const controller of farm.controllers) {
        for (const router of controller.routers) {
          if (router.lat != null && router.lng != null) pts.push([router.lat, router.lng]);
        }
        for (const zone of controller.zones) pushRing(zone.boundary_geometry);
      }
      for (const s of farm.sensors) {
        if (s.lat != null && s.lng != null) pts.push([s.lat, s.lng]);
      }
      for (const w of farm.weather_stations) {
        if (w.lat != null && w.lng != null) pts.push([w.lat, w.lng]);
      }
    }
    return pts;
  }, [farms]);

  const center: [number, number] = useMemo(() => {
    const withCenter = farms.find((f) => f.center_point);
    if (withCenter?.center_point) {
      return [withCenter.center_point.lat, withCenter.center_point.lng];
    }
    return points[0] ?? [-33.87, 151.21];
  }, [farms, points]);

  const hasAnyPosition = points.length > 0 || undrawnRuns.length > 0;

  if (!hasAnyPosition) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 text-center text-sm text-gray-500"
        style={{ height }}
      >
        <Layers className="h-8 w-8 opacity-30" />
        <p className="font-medium text-gray-600">Nothing to map yet</p>
        <p className="max-w-xs text-xs">
          Set locations for your nodes and draw the area each valve waters, and they will appear
          here with their live state.
        </p>
      </div>
    );
  }

  return (
    <div
      className="irrigation-live-map relative overflow-hidden rounded-xl border border-gray-200"
      style={{ height }}
    >
      <MapContainer
        center={center}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
        attributionControl={false}
      >
        <TileLayer url={TILE_LAYERS[layer].url} attribution={TILE_LAYERS[layer].attribution} />
        <FitBounds points={points} fitKey={String(focusFarmId ?? 'all')} />

        {/* Farm boundaries — context, deliberately quiet. */}
        {farms.map((farm) =>
          farm.boundary_geometry?.coordinates?.[0] ? (
            <Polygon
              key={`farm-${farm.id}`}
              positions={ringToLatLng(farm.boundary_geometry.coordinates[0])}
              pathOptions={{
                color: '#F9FAFB',
                weight: 2,
                dashArray: '6 6',
                fillColor: '#F9FAFB',
                fillOpacity: 0.04,
              }}
            >
              <Tooltip sticky>{farm.name}</Tooltip>
            </Polygon>
          ) : null
        )}

        {/* Valve areas. A running one is bright, animated and labelled. */}
        {farms.flatMap((farm) =>
          farm.controllers.flatMap((controller) =>
            controller.zones
              .filter((z) => z.boundary_geometry?.coordinates?.[0])
              .map((zone) => {
                const key = `${zone.router_mac}-${zone.relay_index}`;
                const run = runByKey.get(key);
                const name = relayDisplayName(
                  zone.zone_name,
                  controller.routers.find((r) => r.router_mac === zone.router_mac)?.relay_labels,
                  zone.relay_index
                );
                const on = Boolean(run);
                // A run the hardware has not confirmed is drawn, but quietly:
                // full brightness and the pulse are reserved for water we know
                // is on the ground.
                const confirmed = run?.confirmed ?? false;
                return (
                  <Polygon
                    key={`zone-${key}`}
                    positions={ringToLatLng(zone.boundary_geometry!.coordinates[0])}
                    pathOptions={{
                      color: on ? zone.zone_color : '#E5E7EB',
                      weight: on ? 3 : 1.5,
                      dashArray: on ? '8 6' : undefined,
                      fillColor: zone.zone_color,
                      fillOpacity: on ? (confirmed ? 0.5 : 0.25) : 0.1,
                      className: confirmed ? 'zone-running' : undefined,
                    }}
                  >
                    {on ? (
                      <Tooltip permanent direction="center" className="zone-run-label">
                        {name}
                        {' · '}
                        {run!.remainingSec != null
                          ? `${formatRunTime(run!.remainingSec)} left`
                          : 'manual run'}
                        {!confirmed && ' · due'}
                      </Tooltip>
                    ) : (
                      <Tooltip sticky>
                        {name} — idle
                      </Tooltip>
                    )}
                  </Polygon>
                );
              })
          )
        )}

        {/* Running valves nobody has drawn an area for. */}
        {undrawnRuns.map((run) => (
          <CircleMarker
            key={`undrawn-${run.key}`}
            center={[run.lat!, run.lng!]}
            radius={11}
            pathOptions={{
              color: run.zoneColor,
              fillColor: run.zoneColor,
              fillOpacity: run.confirmed ? 0.35 : 0.15,
              weight: 3,
              className: run.confirmed ? 'run-pulse' : undefined,
            }}
          >
            <Tooltip permanent direction="top" className="zone-run-label">
              {run.zoneName}
              {' · '}
              {run.remainingSec != null ? `${formatRunTime(run.remainingSec)} left` : 'manual run'}
              {!run.confirmed && ' · due'}
              {' · no area drawn'}
            </Tooltip>
          </CircleMarker>
        ))}

        {/* Nodes. */}
        {farms.flatMap((farm) =>
          farm.controllers.flatMap((controller) =>
            controller.routers
              .filter((r) => r.lat != null && r.lng != null)
              .map((router) => (
                <CircleMarker
                  key={`router-${router.router_mac}`}
                  center={[router.lat!, router.lng!]}
                  radius={5}
                  pathOptions={{
                    color: '#FFFFFF',
                    fillColor: router.online ? '#22C55E' : '#9CA3AF',
                    fillOpacity: 1,
                    weight: 2,
                  }}
                >
                  <Tooltip>
                    {router.name || router.router_mac} — {nodeStateLabel(nodeState(router)).toLowerCase()}
                  </Tooltip>
                </CircleMarker>
              ))
          )
        )}

        {/* Sensors, with their latest reading in the tooltip. */}
        {farms.flatMap((farm) =>
          farm.sensors
            .filter((s) => s.lat != null && s.lng != null)
            .map((sensor) => (
              <CircleMarker
                key={`sensor-${sensor.device_id}`}
                center={[sensor.lat!, sensor.lng!]}
                radius={5}
                pathOptions={{
                  color: '#FFFFFF',
                  fillColor: sensor.online ? '#3B82F6' : '#93C5FD',
                  fillOpacity: 1,
                  weight: 2,
                }}
              >
                <Tooltip>
                  {sensor.name || sensor.device_id}
                  {sensor.latest_value != null &&
                    ` — ${sensor.latest_value}${sensor.type_unit || ''}`}
                </Tooltip>
              </CircleMarker>
            ))
        )}

        {/* Weather stations. */}
        {farms.flatMap((farm) =>
          farm.weather_stations
            .filter((w) => w.lat != null && w.lng != null)
            .map((station) => (
              <CircleMarker
                key={`wx-${station.id}`}
                center={[station.lat!, station.lng!]}
                radius={6}
                pathOptions={{
                  color: '#FFFFFF',
                  fillColor: '#F59E0B',
                  fillOpacity: 1,
                  weight: 2,
                }}
              >
                <Tooltip>
                  {station.name}
                  {toNum(station.latest_observation?.temp_c) != null &&
                    ` — ${formatNum(station.latest_observation?.temp_c, 1)}°C`}
                </Tooltip>
              </CircleMarker>
            ))
        )}
        <MapTouchGate />
      </MapContainer>

      {/* Base layer toggle. */}
      <button
        type="button"
        onClick={() => setLayer((l) => (l === 'street' ? 'satellite' : 'street'))}
        className="absolute right-3 top-3 z-[400] flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-white"
      >
        <Layers className="h-3.5 w-3.5" />
        {layer === 'street' ? 'Satellite' : 'Map'}
      </button>

      {/* Legend. */}
      <div className="pointer-events-none absolute bottom-3 left-3 z-[400] rounded-lg border border-gray-200 bg-white/92 px-3 py-2 text-[11px] leading-relaxed text-gray-700 shadow-sm">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
          Watering now
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm border border-gray-300 bg-gray-200" />
          Idle valve area
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
          Node online
        </div>
      </div>
    </div>
  );
}
